import { zodResolver } from '@hookform/resolvers/zod'
import { CircleCheckIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Sku } from '@/interfaces/catalog/Sku'
import API from '@/lib/api'
import { extractErrorCode, extractErrorMessage } from '@/lib/api/errorUtils'
import { webalize } from '@/lib/catalog/webalize'
import { components } from '@invo.space/shared'

import { SkuCreateFormSchema, SkuCreateFormValues } from './schema'

const DUPLICATE_ENTITY_ERROR_CODE = 40013

export interface SkuCreateFormProps {
  productId: string
  productSlug: string
  onSuccess?: (sku: Sku) => void
  onCancel?: () => void
  onSubmittingChange?: (isSubmitting: boolean) => void
}

export const SkuCreateForm = ({
  productId,
  productSlug,
  onSuccess,
  onCancel,
  onSubmittingChange,
}: SkuCreateFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.products.detail.skus.create',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const codeManuallyEdited = useRef(false)

  const methods = useForm<SkuCreateFormValues>({
    resolver: zodResolver(SkuCreateFormSchema),
    defaultValues: {
      name: '',
      code: '',
    },
  })

  const handleNameChange = (name: string) => {
    if (codeManuallyEdited.current) {
      return
    }

    const slug = webalize(name)
    methods.setValue('code', slug ? `${productSlug}-${slug}` : '', {
      shouldValidate: Boolean(name),
    })
  }

  const onSubmit = async (values: SkuCreateFormValues) => {
    setIsSubmitting(true)
    onSubmittingChange?.(true)

    try {
      const payload: components['schemas']['SkuCreate'] = {
        name: values.name,
        code: values.code,
        productId,
        isBundle: false,
        isActive: true,
      }

      const { data } = await API.post<Sku>('/api/v2/skus', payload)
      toast.success(t('success'))
      methods.reset()
      codeManuallyEdited.current = false
      onSuccess?.(data)
    } catch (error: unknown) {
      console.error('SKU create error:', error)

      if (extractErrorCode(error) === DUPLICATE_ENTITY_ERROR_CODE) {
        methods.setError('code', {
          type: 'manual',
          message: t('form.code.duplicate'),
        })
        toast.error(t('error'), {
          description: t('form.code.duplicate'),
        })
        return
      }

      const errorMessage = extractErrorMessage(error)
      toast.error(t('error'), {
        description: errorMessage,
      })
    } finally {
      setIsSubmitting(false)
      onSubmittingChange?.(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={methods.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.name.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('form.name.placeholder')}
                  {...field}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    field.onChange(event)
                    handleNameChange(event.target.value)
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={methods.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.code.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('form.code.placeholder')}
                  {...field}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    codeManuallyEdited.current = true
                    field.onChange(event)
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              {t('form.cancel')}
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            <CircleCheckIcon className="size-4 mr-2" />
            {t('form.submit')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
